import React from 'react';
import { 
  FiPercent, 
  FiFileText, 
  FiClock, 
  FiAlertTriangle,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import LoanVolumeConversionChart from './LoanVolumeConversionChart';
import ApplicationDecisionTimeChart from './ApplicationDecisionTimeChart';
import PerformanceByLoanTypeChart from './PerformanceByLoanTypeChart';
import ProcessingTimeByStageChart from './ProcessingTimeByStageChart';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  target: string;
  icon: React.ReactNode;
  isPositive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  target, 
  icon, 
  isPositive 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">
              {icon}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">{value}</p>
            <div className="flex items-center space-x-2">
              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <FiArrowUp className="mr-1" size={12} />
                ) : (
                  <FiArrowDown className="mr-1" size={12} />
                )}
                {change}
              </span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-slate-200">
            <p className="text-slate-500 text-xs sm:text-sm">
              Target: <span className="font-medium text-slate-700">{target}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoanPerformance: React.FC = () => {
  const performanceMetrics = [
    {
      title: 'Conversion Rate',
      value: '25.2%',
      change: '+0.8%',
      target: '35%',
      icon: <FiPercent className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Applications (Monthly)',
      value: '1,250',
      change: '-2.3%',
      target: '1,400',
      icon: <FiFileText className="text-slate-600" size={20} />,
      isPositive: false
    },
    {
      title: 'Avg Decision Time',
      value: '3.5 days',
      change: '-16.7%',
      target: '2.5 days',
      icon: <FiClock className="text-slate-600" size={20} />,
      isPositive: false
    },
    {
      title: 'Default Rate',
      value: '3.2%',
      change: '-6.3%',
      target: '<3.0%',
      icon: <FiAlertTriangle className="text-slate-600" size={20} />,
      isPositive: false
    }
  ];

  const detailedMetrics = [
    {
      loanType: 'Auto Loans',
      applications: 450,
      funded: 135,
      conversionRate: '30%',
      defaultRate: '2.1%',
      defaultColor: 'text-green-600'
    },
    {
      loanType: 'Personal Loans',
      applications: 380,
      funded: 76,
      conversionRate: '20%',
      defaultRate: '4.2%',
      defaultColor: 'text-yellow-600'
    },
    {
      loanType: 'Home Loans',
      applications: 220,
      funded: 66,
      conversionRate: '30%',
      defaultRate: '1.1%',
      defaultColor: 'text-green-600'
    },
    {
      loanType: 'Credit Cards',
      applications: 200,
      funded: 38,
      conversionRate: '19%',
      defaultRate: '6.8%',
      defaultColor: 'text-red-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Metrics Cards */}
      <div>
        <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan performance metrics and insights</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {performanceMetrics.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>
      </div>

      {/* Loan Volume & Application Decision Time Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Volume & Conversion Rate</h4>
          <div className="h-96 sm:h-80">
            <LoanVolumeConversionChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Application to Decision Time</h4>
          <div className="h-96 sm:h-80">
            <ApplicationDecisionTimeChart />
          </div>
        </div>
      </div>

      {/* Performance by Loan Type & Processing Time Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Performance by Loan Type</h4>
          <div className="h-96 sm:h-80">
            <PerformanceByLoanTypeChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Processing Time by Stage</h4>
          <div className="h-96 sm:h-80">
            <ProcessingTimeByStageChart />
          </div>
        </div>
      </div>

      {/* Detailed Loan Performance Metrics Table */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Detailed Loan Performance Metrics</h4>
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full min-w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Loan Type</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Applications</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Funded</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Conversion Rate</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Default Rate</th>
              </tr>
            </thead>
            <tbody>
              {detailedMetrics.map((metric, index) => (
                <tr key={index} className="border-b border-slate-100">
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-slate-700 font-medium text-xs sm:text-sm">{metric.loanType}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{metric.applications.toLocaleString()}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{metric.funded}</td>
                  <td className="py-2 sm:py-3 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{metric.conversionRate}</td>
                  <td className={`py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm ${metric.defaultColor}`}>{metric.defaultRate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LoanPerformance;